import './style.scss';

import { FilterTwoTone } from '@ant-design/icons';
import { Button } from '@components/Button/Button';
import { tableColumnsProcessed } from '@components/common/UTable/UTableUtils';
import { Filter } from '@components/Filter/Filter';
import { IFormItem } from '@components/Form/FormItem/FormItem';
import SortDefaultIcon from '@resource/SortDefaultIcon.png';
import { Table as AntTable, Tooltip, Typography } from 'antd';
import { ButtonType } from 'antd/es/button/buttonHelpers';
import { ColumnType } from 'antd/es/table';
import {
  FilterValue,
  SorterResult,
  TablePaginationConfig,
} from 'antd/es/table/interface';
import { ColumnGroupType, ExpandableConfig } from 'rc-table/lib/interface';
import React, { useMemo, useState } from 'react';

import { Sort } from '@/shared/api/types';
import { Auth, hasPermission, securityConfig } from '@/shared/security/hasPermission';
import { withSecurity } from '@/shared/security/withSecurity';

const { Title } = Typography;

export type PagingType = {
  page: number;
  size: number;
};

export interface ITableProps {
  size?: 'large' | 'middle' | 'small';
  data?: any[];
  columns: ITableColumn[];
  loading?: boolean;
  paging?: PagingType;
  total?: number;
  onPagingChange?: (paging: PagingType) => void;
  filter?: ITableFilter;
  actions?: (record: any) => ITableActionProps[];
  buttons?: ITableButtonProps[];
  expandable?: ExpandableConfig<any>;
  scroll?: {
    scrollToFirstRowOnChange?: boolean;
    x?: string | number | true;
    y?: string | number;
  };
  rowClassName?: (record: any) => string;
  className?: string;
  showTable?: boolean;
  components?: any;
  onChange?: (sorter?: Sort[]) => void;
  currentSort?: Sort[];
  defaultSort?: Sort[];
  actionColumnWidth?: number;
  auth: Auth;
  style?: React.CSSProperties | undefined;
}

export interface IEditableNumberConfig {
  type: 'number';
  step?: number;
  min?: number;
  max?: number;
  precision?: number;
  displayEditButton?: boolean;
  onSave: (record: any, value: number, extra?: string) => void;
  extraField?: ExtraFieldProps;
  width?: number | string;
}

type ExtraFieldProps = {
  title?: string;
  placeholder?: string;
  onChange?: (value: any) => void;
  disabled?: boolean;
  maxLength?: number;
};

export interface ITableColumn extends Omit<ColumnType<any>, 'children'> {
  type?: 'text' | 'date' | 'datetime' | 'select';
  payload?: any;
  children?: ITableColumn[];
  sorterKey?: string;
  editable?: IEditableNumberConfig;
}

export interface ITableActionProps {
  tooltip?: string;
  icon: React.ReactNode;
  action: (record: any) => void;
  popconfirm?: {
    title: string;
    description?: string;
    okText?: string;
    cancelText?: string;
  };
  disabled?: boolean;
  security?: securityConfig;
}

export interface ITableButtonProps {
  text?: string;
  action: () => void;
  addBtn?: boolean;
  className?: string;
  loading?: boolean;
  type?: ButtonType;
  disabled?: boolean;
  security?: securityConfig;
}

interface ITableFilter {
  filters?: Record<string, any>;
  schema: ITableFilterItem[];
  onFilter: (filters: Record<string, any>) => void;
  onReset?: () => void;
  security?: securityConfig;
  underTitle?: string | null;
}

export type ITableFilterItem = IFormItem;

const UTableComponent: React.FC<ITableProps> = ({
  size,
  data,
  columns,
  loading,
  paging,
  total,
  onPagingChange,
  filter,
  actions,
  buttons,
  expandable,
  scroll,
  rowClassName,
  className,
  showTable = true,
  components,
  onChange,
  currentSort,
  defaultSort,
  actionColumnWidth,
  auth,
  style,
}) => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const onFilterSubmit = async (values: Record<string, any>) => {
    filter?.onFilter?.(values);
    if (paging) onPagingChange?.({ page: 0, size: paging.size });
  };

  const onFilterReset = () => {
    filter?.onReset?.();
    if (paging) onPagingChange?.({ page: 0, size: paging.size });
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<any> | SorterResult<any>[],
  ) => {
    if (paging && pagination.current && pagination.pageSize) {
      const newPage = pagination.current - 1;
      const newSize = pagination.pageSize;

      if (newPage !== paging.page || newSize !== paging.size) {
        onPagingChange?.({ page: newPage, size: newSize });
        return;
      }
    }

    if (paging) {
      onPagingChange?.({ page: 0, size: paging.size });
    }

    let sort: Sort[] | undefined = undefined;

    if (Array.isArray(sorter)) {
      sort = sorter
        ?.filter((s) => s?.field && s?.order)
        ?.map((s) => {
          const field = Array.isArray(s.field) ? s.field[0] : (s.field as string);
          return {
            key: field,
            sortKey: ((s?.column as ITableColumn)?.sorterKey as string) ?? field,
            order: s.order === 'ascend' ? 'asc' : 'desc',
          };
        });
    } else {
      if (sorter?.field && sorter?.order) {
        const field = Array.isArray(sorter.field)
          ? sorter.field[0]
          : (sorter.field as string);

        sort = [
          {
            key: field,
            sortKey: ((sorter?.column as ITableColumn)?.sorterKey as string) ?? field,
            order: sorter.order === 'ascend' ? 'asc' : 'desc',
          },
        ];
      }
    }

    onChange?.(sort);
  };

  const processedColumns = useMemo(() => {
    const baseColumns = tableColumnsProcessed(columns, auth, actions, actionColumnWidth);

    return baseColumns.map((col: ITableColumn, i: number) => {
      const sortI = currentSort?.findIndex((s: Sort) => col.key === s.key) ?? -1;
      const sort = sortI >= 0 ? currentSort?.[sortI] : undefined;

      if (currentSort && sort) {
        return {
          ...col,
          align: 'center',
          sorter: { multiple: currentSort.length - sortI },
          sortOrder: sort.order === 'asc' ? 'ascend' : 'descend',
        };
      }

      return {
        ...col,
        align: 'center',
        sorter: col?.sorter ? { multiple: i + (currentSort?.length ?? 1) } : undefined,
        sortOrder: null,
      };
    }) as (ColumnGroupType<any> | ColumnType<any>)[];
  }, [columns, actions, currentSort]);

  const sortable = columns.some((col) => col.sorter);

  return (
    <div className={'table-wrapper'}>
      {(buttons || filter) && (
        <div className={'table-buttons-wrapper'}>
          {buttons
            ?.filter((btn) => hasPermission(auth, btn?.security))
            .map((button, index) => {
              if (button.addBtn) {
                return (
                  <Button
                    key={index}
                    type={button?.type ?? 'primary'}
                    text={button?.text ?? 'Добавить'}
                    action={button.action}
                  />
                );
              } else {
                return (
                  <Button
                    key={index}
                    className={button.className}
                    text={button.text ?? ''}
                    action={button.action}
                    loading={button.loading}
                    type={button.type}
                    disabled={button.disabled}
                  />
                );
              }
            })}
          <div className={'table-buttons-filter-wrapper'}>
            {filter && (
              <Tooltip title={isFilterOpen ? 'Скрыть фильтры' : 'Показать фильтры'}>
                <Button
                  className={'table-buttons-filter'}
                  type={'text'}
                  icon={
                    <FilterTwoTone
                      className="filter-icon"
                      twoToneColor={`${Object.keys(filter?.filters || {}).length ? '#046094' : 'rgb(255 255 255) rgb(73, 78, 80)'}`}
                    />
                  }
                  onClick={() => setIsFilterOpen(!isFilterOpen)}
                  display={hasPermission(auth, filter?.security)}
                />
              </Tooltip>
            )}
            {sortable && (
              <Tooltip title="Сортировка по умолчанию">
                <Button
                  className={'table-buttons-filter'}
                  type={'text'}
                  icon={
                    <img
                      src={SortDefaultIcon}
                      alt="Сбросить сортировку"
                      className={'table-buttons-default-sort-icon'}
                    />
                  }
                  styles={{ icon: { fontSize: 0 } }}
                  onClick={() => onChange?.(defaultSort ?? [])}
                />
              </Tooltip>
            )}
          </div>
        </div>
      )}
      {filter && (
        <div className={'table-filter-wrapper'}>
          <Filter
            open={isFilterOpen}
            filters={filter.filters}
            schema={filter.schema}
            onSubmit={onFilterSubmit}
            onReset={onFilterReset}
          />
          {filter?.underTitle && (
            <Title className={'table-filter-title'} level={4}>
              {filter.underTitle}
            </Title>
          )}
        </div>
      )}
      {showTable && (
        <AntTable
          bordered
          size={size}
          rowKey={'id'}
          className={`table-content ${className ?? ''}`}
          loading={loading}
          columns={processedColumns}
          dataSource={data ?? []}
          scroll={{ x: 'max-content', ...scroll }}
          expandable={expandable}
          components={components}
          style={style}
          onChange={handleTableChange}
          pagination={
            paging
              ? {
                  size: 'default',
                  position: ['bottomRight'],
                  current: paging.page + 1,
                  pageSize: paging.size,
                  total,
                  pageSizeOptions: [10, 20, 50],
                  defaultPageSize: 10,
                  showSizeChanger: true,
                  hideOnSinglePage: false,
                  onChange: (page, size) => onPagingChange?.({ page: page - 1, size }),
                  showTotal: (total, range) => `${range[0]}-${range[1]} из ${total}`,
                }
              : false
          }
          rowClassName={rowClassName}
        />
      )}
    </div>
  );
};

export const UTable = withSecurity(UTableComponent);
